import { gql, ApolloCache } from '@apollo/client';
import { useQuery } from 'platform/useQuery';
import {
  OdProtosCommonPagination_PaginationRequestInput as PaginationRequestInput,
  OdProtosCommonPagination_OffsetPaginationRequestInput as OffsetPaginationRequestInput,
  OdProtosBuyerData_User_PropertiesFilterInput,
  OdProtosBuyerServicesBuyer_PropertyRequests_GetFeedPropertiesForCurrentUser_SortKey as FeedSortKey,
} from '__generated__/globalTypes';
import {
  GetFavoritePropertiesForCurrentUser,
  GetFavoritePropertiesForCurrentUserVariables,
  GetFavoritePropertiesForCurrentUser_buyer_getFavoritePropertiesForCurrentUser_properties_openHouses,
} from 'buying/graphql/__generated__/GetFavoritePropertiesForCurrentUser';
import {
  GetFeedPropertiesForCurrentUser,
  GetFeedPropertiesForCurrentUserVariables,
  GetFeedPropertiesForCurrentUser_buyer_getFeedPropertiesForCurrentUser_properties_openHouses,
} from 'buying/graphql/__generated__/GetFeedPropertiesForCurrentUser';
import {
  GetPropertiesStats,
  GetPropertiesStatsVariables,
} from 'buying/graphql/__generated__/GetPropertiesStats';
import { BuyerCurrentUser_buyer_getCurrentUser_user_feedPropertiesFilter as PropertiesFilter } from 'buying/graphql/__generated__/BuyerCurrentUser';

import { PropertyCardFragment } from 'buying/graphql/fragments/PropertyCardFragment';
import { PropertyCardFragment as PropertyCardType } from 'buying/graphql/fragments/__generated__/PropertyCardFragment';

export type PropertyCardProperty = PropertyCardType;

export type OpenHouse =
  | GetFeedPropertiesForCurrentUser_buyer_getFeedPropertiesForCurrentUser_properties_openHouses
  | GetFavoritePropertiesForCurrentUser_buyer_getFavoritePropertiesForCurrentUser_properties_openHouses;

export const getFavoritePropertiesQuery = gql`
  query GetFavoritePropertiesForCurrentUser(
    $input: OdProtosBuyerServicesBuyer_PropertyRequests_GetFavoritePropertiesForCurrentUserInput!
  ) {
    buyer {
      getFavoritePropertiesForCurrentUser(input: $input) {
        properties {
          ...PropertyCardFragment
        }
        paginationResponse {
          hasNextPage
          hasPreviousPage
          cursors
        }
      }
    }
  }
  ${PropertyCardFragment}
`;

/** Evict GetFavoritePropertiesForCurrentUser from the Apollo cache */
export const evictGetFavoritePropertiesForCurrentUserFromCache = <T>(cache: ApolloCache<T>) => {
  cache.modify({
    id: 'ROOT_QUERY',
    fields: {
      buyer(oldCache) {
        const newCache = { ...oldCache };
        const queryWithArgs = Object.keys(newCache).find((queryName) =>
          queryName.startsWith('getFavoritePropertiesForCurrentUser('),
        );
        if (queryWithArgs) {
          delete newCache[queryWithArgs];
        }
        return newCache;
      },
    },
  });
};

export const getFeedPropertiesQuery = gql`
  query GetFeedPropertiesForCurrentUser(
    $input: OdProtosBuyerServicesBuyer_PropertyRequests_GetFeedPropertiesForCurrentUserInput!
  ) {
    buyer {
      getFeedPropertiesForCurrentUser(input: $input) {
        properties {
          ...PropertyCardFragment
        }
        paginationResponse {
          hasNextPage
          hasPreviousPage
        }
      }
    }
  }
  ${PropertyCardFragment}
`;

export const getPropertiesStats = gql`
  query GetPropertiesStats(
    $input: OdProtosBuyerServicesBuyer_PropertyRequests_GetPropertiesStatsForPropertiesFilterInput!
  ) {
    buyer {
      getPropertiesStatsForPropertiesFilter(input: $input) {
        avgUpdatesPerWeek
        currentNumberOfProperties
      }
    }
  }
`;

/** used to convert BuyerCurrentUser_buyer_getCurrentUser_user_feedPropertiesFilter type to OdProtosBuyerData_User_PropertiesFilterInput */
export const convertBuyerUserFiltersToPropertiesStatsInput = (
  filter?: PropertiesFilter,
): OdProtosBuyerData_User_PropertiesFilterInput | undefined => {
  if (!filter) {
    return undefined;
  }
  return {
    zones: filter.zones.map((z) => ({ gdsId: z.gdsId })),
    propertyTypes: filter.propertyTypes,
    minPrice: filter.minPrice,
    maxPrice: filter.maxPrice,
    minBedrooms: filter.minBedrooms,
    minBathrooms: filter.minBathrooms,
    minSqft: filter.minSqft,
    maxSqft: filter.maxSqft,
    minLotSqft: filter.minLotSqft,
    maxLotSqft: filter.maxLotSqft,
    minYearBuilt: filter.minYearBuilt,
    maxYearBuilt: filter.maxYearBuilt,
    maxMonthlyHoaFee: filter.maxMonthlyHoaFee,
    isSingleStory: filter.isSingleStory,
    isFhaEligible: filter.isFhaEligible,
    hasPool: filter.hasPool,
    onlyHomebuilder: filter.onlyHomebuilder,
  };
};

/** Fetch properties a user has favorited */
export const useGetFavoriteProperties = (paginationRequest?: PaginationRequestInput) => {
  const { data, loading } = useQuery<
    GetFavoritePropertiesForCurrentUser,
    GetFavoritePropertiesForCurrentUserVariables
  >(getFavoritePropertiesQuery, {
    variables: {
      input: {
        paginationRequest,
      },
    },
    fetchPolicy: 'cache-first',
  });

  return {
    properties: data?.buyer.getFavoritePropertiesForCurrentUser?.properties || [],
    paginationResponse: data?.buyer.getFavoritePropertiesForCurrentUser?.paginationResponse,
    loading,
  };
};

/** Fetch feed properties for current user */
export const useGetFeedProperties = (
  paginationRequest?: OffsetPaginationRequestInput,
  { sortKey, enableCache }: { sortKey?: FeedSortKey; enableCache?: boolean } = {},
) => {
  const { data, ...rest } = useQuery<
    GetFeedPropertiesForCurrentUser,
    GetFeedPropertiesForCurrentUserVariables
  >(getFeedPropertiesQuery, {
    variables: {
      input: {
        paginationRequest,
        sortKey,
      },
    },
    fetchPolicy: enableCache ? 'cache-first' : 'network-only',
  });
  return {
    properties: data?.buyer.getFeedPropertiesForCurrentUser?.properties || [],
    paginationResponse: data?.buyer.getFeedPropertiesForCurrentUser?.paginationResponse,
    ...rest,
  };
};

/** Gets Properties stats info.. */
export const usePropertiesStats = (propertyFilter?: OdProtosBuyerData_User_PropertiesFilterInput) =>
  useQuery<GetPropertiesStats, GetPropertiesStatsVariables>(getPropertiesStats, {
    variables: {
      input: {
        feedPropertiesFilter: propertyFilter,
      },
    },
    fetchPolicy: 'no-cache',
    skip: !propertyFilter,
  });
